import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { API_URLS } from 'app-constants/api';
import { store } from 'store/store';
import { authActions } from 'store/auth/slice';
import { AccessToken } from 'store/auth/types';
import { localStorageService } from './local-storage.service';
import { tokenService } from './token.service';

export class HTTPService {
  static AUTH_HEADER_KEY = 'Authorization';

  static ssoHints = ['hideez', 'local-storage', 'shield'];

  private static isReloadingForAuthError = false;

  static UNAUTHORIZED = 401;

  instance: AxiosInstance;

  constructor(baseURL?: string) {
    this.instance = axios.create({
      baseURL,
      timeout: 300000,
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
    });
  }

  post<Request, Response>(url: string, data?: Request, config?: AxiosRequestConfig): Promise<AxiosResponse<Response>> {
    return this.instance.post(url, data, config);
  }

  get<Request, Response>(
    url: string,
    params: Request,
    paramsSerializer?: (queryParams: any) => string
  ): Promise<AxiosResponse<Response>> {
    return this.instance.get(url, { params, paramsSerializer });
  }

  getBlob<Request>(url: string, params: Request, paramsSerializer?: (q: any) => string) {
    return this.instance.get(url, { params, paramsSerializer, responseType: 'blob' });
  }

  put<Request, Response>(url: string, data?: Request): Promise<AxiosResponse<Response>> {
    return this.instance.put(url, JSON.stringify(data));
  }

  patch<Request, Response>(url: string, data?: Request): Promise<AxiosResponse<Response>> {
    return this.instance.patch(url, data);
  }

  delete<Request, Response>(url: string, params?: Request): Promise<AxiosResponse<Response>> {
    return this.instance.delete(url, params);
  }

  head<Request, Response>(url: string, params?: Request): Promise<AxiosResponse<Response>> {
    return this.instance.head(url, { params });
  }

  setAuthHeader(token: AccessToken) {
    this.instance.defaults.headers.common[HTTPService.AUTH_HEADER_KEY] = `Bearer ${token}`;
  }

  removeAuthHeader() {
    this.instance.defaults.headers.common[HTTPService.AUTH_HEADER_KEY] = '';
  }

  getDocumentInBase64<Response>(
    url: string,
    params?: Request,
    paramsSerializer?: (queryParams: any) => string
  ): Promise<AxiosResponse<Response>> {
    return this.instance.get(url, {
      params,
      paramsSerializer,
      responseType: 'arraybuffer',
    });
  }

  getDocumentInBlob<Request, Response>(
    url: string,
    params?: Request,
    paramsSerializer?: (queryParams: any) => string
  ): Promise<AxiosResponse<Response>> {
    return this.instance.get(url, {
      params,
      paramsSerializer,
      responseType: 'blob',
    });
  }

  setupInterceptors(): void {
    this.instance.interceptors.request.use((request) => {
      const originalRequest = request;
      const accessToken = localStorageService.getAccessToken();
      const isAuthEndpoint = HTTPService.isAuthRelatedUrl(request.url);

      if (originalRequest?.headers && accessToken && !isAuthEndpoint) {
        originalRequest.headers[HTTPService.AUTH_HEADER_KEY] = `Bearer ${accessToken}`;
      }

      return originalRequest;
    });

    this.instance.interceptors.response.use(
      (response) => {
        if (HTTPService.isSsoRedirectResponse(response)) {
          this.handleSessionExpiration();
        }

        return response;
      },
      (error: AxiosError) => {
        const requestUrl = error.config?.url;

        if (requestUrl === API_URLS.refreshAccessToken()) {
          this.handleSessionExpiration();
          return Promise.reject(error);
        }

        if (error.response?.status === HTTPService.UNAUTHORIZED && requestUrl !== API_URLS.login()) {
          return tokenService.addNewAccessTokenToErrorRequest(error);
        }

        throw error;
      }
    );
  }

  static isAuthRelatedUrl(url?: string): boolean {
    return url === API_URLS.login() || url === API_URLS.refreshAccessToken();
  }

  static isSsoRedirectResponse(response: AxiosResponse): boolean {
    const responseUrl = response?.request?.responseURL;

    if (!responseUrl) {
      return false;
    }

    if (HTTPService.isAuthRelatedUrl(response.config?.url)) {
      return false;
    }

    const isDifferentUrl = responseUrl !== response.config?.url;
    const looksLikeSso = HTTPService.ssoHints.some((hint) => responseUrl.toLowerCase().includes(hint));

    return isDifferentUrl && looksLikeSso;
  }

  private handleSessionExpiration() {
    if (HTTPService.isReloadingForAuthError) {
      return;
    }

    HTTPService.isReloadingForAuthError = true;

    localStorageService.clearJwtTokens();
    this.removeAuthHeader();

    store.dispatch(authActions.requestLogout());

    window.location.reload();
  }
}

const httpService = new HTTPService(API_URLS.base);

export default httpService;
